<template>
  <Transition name="toast">
    <div
      v-if="visible"
      class="toast-wrapper"
      :class="{
        'toast-wrapper--bottom': placement === 'bottom',
        'toast-wrapper--fixed-at': placement === 'fixed-at',
      }"
      :style="fixedAtStyle"
    >
      <div class="toast" :class="[type, layoutClass]">
        <span v-if="type === 'success'" class="toast-success-icon" aria-hidden="true">
          <svg
            class="toast-success-check"
            width="9"
            height="8"
            viewBox="0 0 9 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 4.2 3.2 6.4 8 1.5"
              stroke="#FFFFFF"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
        <img v-else :src="iconSrc" :alt="type" class="toast-icon" width="16" height="16" />
        <span class="toast-text">{{ message }}</span>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  visible: boolean
  type: 'success' | 'warning' | 'error'
  message: string
  placement?: 'top' | 'bottom' | 'fixed-at'
  fixedAt?: { top: string; left: string } | null
}

const props = withDefaults(defineProps<Props>(), {
  placement: 'top',
  fixedAt: null,
})

const fixedAtStyle = computed(() => {
  if (props.placement !== 'fixed-at' || !props.fixedAt) return undefined
  return {
    top: props.fixedAt.top,
    left: props.fixedAt.left,
    right: 'auto',
    bottom: 'auto',
    justifyContent: 'flex-start',
  }
})

const iconMap = {
  error: new URL('@/assets/error.png', import.meta.url).href,
  warning: new URL('@/assets/warning.png', import.meta.url).href,
}

const iconSrc = computed(() => iconMap[props.type as keyof typeof iconMap] ?? '')

const SCHEDULE_SUCCESS_MESSAGES = new Set([
  'Successfully scheduled',
  'Successfully reassigned',
  'Successfully canceled',
])

const layoutClass = computed(() => {
  if (props.type === 'success') {
    if (SCHEDULE_SUCCESS_MESSAGES.has(props.message.trim())) return 'toast--schedule-success'
    return 'toast--success'
  }
  if (props.type === 'error') return 'toast--error-wide'
  const m = props.message.trim()
  if (m === 'Order ID already exists' || m === 'Home ID already exists') {
    return 'toast--warning-md'
  }
  if (
    m === 'Please complete all required fields.' ||
    m === 'Please select a time slot' ||
    m === 'Please select an order first.' ||
    m === 'Please select an inspector first.' ||
    m === 'Please select a date first.' ||
    m === 'Please unassign the current schedule first'
  ) {
    return 'toast--warning-xl'
  }
  return 'toast--warning-xl'
})
</script>

<style scoped>
.toast-wrapper {
  position: fixed;
  top: 24px;
  bottom: auto;
  left: 0;
  right: 0;
  z-index: 13050;
  display: flex;
  justify-content: center;
  pointer-events: none;
  overflow: visible;
}

.toast {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  box-sizing: border-box;
  min-height: 48px;
  padding: 14px 16px 14px 20px;
  background: #ffffff;
  border-radius: 4px;
  border-left: 5px solid transparent;
  box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: visible;
  pointer-events: auto;
}

.toast.success.toast--success,
.toast.success.toast--schedule-success {
  border-left-color: #98d5be;
}

.toast.error.toast--error-wide {
  border-left-color: #ff6b6b;
}

.toast.warning.toast--warning-xl,
.toast.warning.toast--warning-md {
  border-left-color: #f5a623;
}

.toast-icon,
.toast-success-icon {
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
  max-width: 16px;
  max-height: 16px;
  flex: 0 0 16px;
  flex-shrink: 0;
  box-sizing: border-box;
}

.toast-icon {
  display: block;
  object-fit: contain;
}

.toast-success-icon {
  border-radius: 50%;
  background: #98d5be;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.toast-success-check {
  display: block;
  width: 9px;
  height: 8px;
  min-width: 9px;
  min-height: 8px;
  max-width: 9px;
  max-height: 8px;
  flex-shrink: 0;
}

.toast-text {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: SFPro, SFPro;
  font-weight: 400;
  font-size: 14px;
  font-style: normal;
  line-height: 20px;
  color: #1f2d42;
  text-align: left;
  white-space: nowrap;
  flex: 0 0 auto;
  width: auto;
}

.toast-enter-active,
.toast-leave-active {
  transition: opacity 0.28s ease;
}

.toast-enter-active .toast,
.toast-leave-active .toast {
  transition:
    opacity 0.28s ease,
    transform 0.28s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
}

.toast-wrapper--bottom {
  top: auto;
  bottom: 24px;
}

.toast-wrapper--fixed-at {
  justify-content: flex-start;
}

.toast-wrapper--fixed-at .toast-enter-from,
.toast-wrapper--fixed-at .toast-leave-to {
  transform: translateY(8px);
}

.toast-enter-from .toast,
.toast-leave-to .toast {
  opacity: 0;
  transform: translateY(-16px);
}

.toast-wrapper--bottom .toast-enter-from .toast,
.toast-wrapper--bottom .toast-leave-to .toast {
  transform: translateY(16px);
}
</style>
